import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { useNotification } from '../../hooks/useNotification'
import services from '../../services'
import BuscaPessoa from '../../novo/componentes/BuscaPessoa'
import AvisoGuiaNovo, { avisarGuiaBaixado } from './AvisoGuiaNovo'
import CardBook from './CardBook'
import Janela from '../../novo/componentes/Janela'
import {
  Analise, DIAS, Destinatario, Disparo, DisparoEntrada, FREQUENCIAS, Frequencia, FiltroDoDisparo, Opcoes, PERIODOS,
  descreverRecorrencia, disparoVazio, lerValores, paraEntrada, pendencias, podarFiltros
} from './disparos'
import './abaDisparos.scss'

interface Envio { userId: number; nome?: string | null; status: string; detalhe?: string | null; quando: string }
interface Previa { assunto: string; html: string; blocosComFalha: number }

const dataHora = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—')

const STATUS_ENVIO: Record<string, { rotulo: string; classe: string }> = {
  sent: { rotulo: 'Enviado', classe: 'ok' },
  processing: { rotulo: 'Processando', classe: 'processando' },
  failed: { rotulo: 'Falhou', classe: 'inativo' },
  skipped: { rotulo: 'Não enviado', classe: 'alerta' }
}

/** Baixa um texto como arquivo, sem passar por link do servidor. */
function baixarTexto(nomeArquivo: string, conteudo: string) {
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/markdown;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = nomeArquivo
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Disparos de e-mail do dashboard: o e-mail é um HTML (feito no Claude, com o bloco portal-email), enviado na recorrência escolhida
 * para quem tem login. O portal desenha no servidor, na hora do envio, e cada pessoa recebe os números no recorte da própria regra,
 * com as cores, a fonte e o logo do template. A prévia e o teste mostram o que quem pede veria.
 */
export default function AbaDisparos({ dashboardId, dicionario }: { dashboardId: number; dicionario: { colunas: { nome: string }[]; medidas: { nome: string }[] } }) {
  const { notify } = useNotification()
  const [opcoes, setOpcoes] = useState<Opcoes | null>(null)
  const [erroOpcoes, setErroOpcoes] = useState<string | null>(null)
  const [lista, setLista] = useState<Disparo[] | null>(null)
  const [editando, setEditando] = useState<{ id: number | null; dados: DisparoEntrada } | null>(null)
  const [pessoas, setPessoas] = useState<Record<number, Destinatario>>({})
  const [analise, setAnalise] = useState<Analise | null>(null)
  const [previa, setPrevia] = useState<Previa | null>(null)
  const [trabalhando, setTrabalhando] = useState(false)
  const [excluir, setExcluir] = useState<Disparo | null>(null)
  const [historico, setHistorico] = useState<{ disparo: Disparo; envios: Envio[] } | null>(null)
  const [valoresTexto, setValoresTexto] = useState<Record<string, string>>({})
  const rodando = useRef<Set<number>>(new Set())
  const historicoId = useRef<number | null>(null)

  const carregarLista = useCallback(async () => {
    const r: any = await services.api.disparosEmailLista(dashboardId)
    if (r.fail) { notify(r.error, { variant: 'error' }); return }
    setLista(r.data)
  }, [dashboardId, notify])

  useEffect(() => {
    let vivo = true
    ;(async () => {
      const r: any = await services.api.disparosEmailOpcoes(dashboardId)
      if (!vivo) return
      if (r.fail) { setErroOpcoes(r.error); return }
      setOpcoes(r.data)
      carregarLista()
    })()
    return () => { vivo = false }
  }, [dashboardId, carregarLista])

  useEffect(() => { historicoId.current = historico?.disparo.id ?? null }, [historico])

  // Enquanto algum disparo estiver processando, a lista se atualiza sozinha.
  const haRodando = lista?.some(d => d.emProcessamento) ?? false
  useEffect(() => {
    if (!haRodando) return
    const relogio = setInterval(carregarLista, 5000)
    return () => clearInterval(relogio)
  }, [haRodando, carregarLista])

  // Quem estava rodando e parou: avisa o resultado e atualiza o histórico se ele estiver aberto (também enquanto roda, para ver o andamento).
  useEffect(() => {
    if (!lista) return
    const agora = new Set(lista.filter(d => d.emProcessamento).map(d => d.id))
    const terminaram = lista.filter(d => rodando.current.has(d.id) && !agora.has(d.id))
    rodando.current = agora
    const aberto = historicoId.current
    const aAtualizar = lista.filter(d => terminaram.includes(d) || (d.id === aberto && agora.has(d.id)))
    aAtualizar.forEach(async d => {
      const h: any = await services.api.disparosEmailHistorico(dashboardId, d.id)
      if (h.fail) return
      const envios = h.data as Envio[]
      setHistorico(a => (a?.disparo.id === d.id ? { disparo: d, envios } : a))
      if (!terminaram.includes(d)) return
      if (envios[0]?.status === 'sent') notify(`Disparo “${d.nome}” concluído.`, { variant: 'success' })
      else notify(`O disparo “${d.nome}” terminou sem enviar. Veja o motivo no histórico.`, { variant: 'warning' })
    })
  }, [lista, dashboardId, notify])

  // A análise roda enquanto a pessoa cola ou escreve, com folga entre as teclas: mostra o bloqueio antes de tentar salvar.
  const layoutAtual = editando?.dados.layout ?? null
  useEffect(() => {
    if (layoutAtual == null || !layoutAtual.trim()) { setAnalise(null); return }
    const relogio = setTimeout(async () => {
      const r: any = await services.api.disparosEmailAnalisar(dashboardId, layoutAtual)
      if (r.fail) return
      const a: Analise = r.data
      setAnalise(a)
      // Filtro que o HTML deixou de declarar sai do disparo (o servidor o recusaria). Com o JSON quebrado não se mexe em nada.
      const jsonQuebrado = a.blocos.length === 0 && a.filtros.length === 0 && a.achados.some(x => x.nivel === 'bloqueio')
      if (!jsonQuebrado)
        setEditando(e => (e ? { ...e, dados: { ...e.dados, config: podarFiltros(e.dados.config, a.filtros.map(f => f.id)) } } : e))
    }, 600)
    return () => clearTimeout(relogio)
  }, [layoutAtual, dashboardId])

  const executar = async (acao: () => Promise<any>, sucesso?: string) => {
    setTrabalhando(true)
    try {
      const r = await acao()
      if (r?.fail) { notify(r.error, { variant: 'error' }); return null }
      if (sucesso) notify(sucesso, { variant: 'success' })
      return r?.data ?? true
    } finally {
      setTrabalhando(false)
    }
  }

  const abrir = (d: Disparo | null) => {
    if (!opcoes) return
    const dados = d ? paraEntrada(d) : disparoVazio(opcoes.ambientes[0]?.id ?? 0)
    setPessoas(Object.fromEntries((d?.destinatarios ?? []).map(x => [x.userId, x])))
    setValoresTexto(Object.fromEntries(dados.config.filtros.filter(f => f.tipo === 'valores').map(f => [f.id, (f.valores ?? []).join('\n')])))
    setPrevia(null)
    setAnalise(null)
    setHistorico(null)
    setEditando({ id: d?.id ?? null, dados })
  }

  const fechar = () => { setEditando(null); setPrevia(null); setAnalise(null) }

  const mudar = (parcial: Partial<DisparoEntrada>) => setEditando(e => (e ? { ...e, dados: { ...e.dados, ...parcial } } : e))

  const salvar = async () => {
    if (!editando) return
    const salvo = await executar(() => services.api.disparosEmailSalvar(dashboardId, editando.id, editando.dados), 'Disparo salvo.')
    if (!salvo) return
    fechar()
    carregarLista()
  }

  const prever = async () => {
    if (!editando) return
    const r = await executar(() => services.api.disparosEmailPrevia(dashboardId, editando.dados))
    if (r) setPrevia(r)
  }

  const comecarDoModelo = async () => {
    if (!editando) return
    const r = await executar(() => services.api.disparosEmailModelo(dashboardId))
    if (r) mudar({ layout: r.html })
  }

  const baixarGuia = async () => {
    const r: any = await services.api.disparosEmailGuia(dashboardId)
    if (r.fail) notify(r.error, { variant: 'error' })
    else {
      baixarTexto(r.data.nomeArquivo, r.data.conteudo)
      avisarGuiaBaixado()
    }
  }

  const baixarDicionario = async () => {
    const r: any = await services.api.relatorioHtmlExportarDicionario(dashboardId)
    if (r.fail) notify(r.error, { variant: 'error' })
    else baixarTexto(r.data.nomeArquivo, r.data.conteudo)
  }

  const alternar = async (d: Disparo) => {
    if (await executar(() => services.api.disparosEmailAtivar(dashboardId, d.id, !d.ativo), d.ativo ? 'Disparo pausado.' : 'Disparo ativado.')) carregarLista()
  }

  // O teste roda em segundo plano no servidor (com book leva minutos): o servidor marca o disparo como "Processando" e a tela
  // recarrega a lista enquanto houver algum rodando, para o botão Testar ficar travado e o resultado aparecer sozinho.
  const testar = async (d: Disparo) => {
    const r = await executar(() => services.api.disparosEmailTeste(dashboardId, d.id))
    if (r) notify('Teste pedido. Com book, o e-mail sai quando o arquivo ficar pronto (alguns minutos). O resultado aparece no histórico.', { variant: 'info' })
    await carregarLista()
    if (!r) return
    const h: any = await services.api.disparosEmailHistorico(dashboardId, d.id)
    if (!h.fail) setHistorico({ disparo: d, envios: h.data })
  }

  const verHistorico = async (d: Disparo) => {
    const r = await executar(() => services.api.disparosEmailHistorico(dashboardId, d.id))
    if (r) setHistorico({ disparo: d, envios: r })
  }

  const apagar = async () => {
    if (!excluir) return
    if (await executar(() => services.api.disparosEmailExcluir(dashboardId, excluir.id), 'Disparo excluído.')) {
      setExcluir(null)
      setHistorico(h => (h?.disparo.id === excluir.id ? null : h))
      carregarLista()
    }
  }

  const faltas = useMemo(() => (editando ? pendencias(editando.dados, analise) : []), [editando, analise])

  if (erroOpcoes) return <div className="dp"><div className="rh-alerta">{erroOpcoes}</div></div>
  if (!opcoes || !lista) return <div className="dp"><p className="ng-explic">Carregando…</p></div>

  // ---------- edição ----------
  if (editando) {
    const d = editando.dados
    const bloqueios = analise?.achados.filter(a => a.nivel === 'bloqueio') ?? []
    const avisos = analise?.achados.filter(a => a.nivel === 'aviso') ?? []
    const filtrosDoHtml = analise?.filtros ?? []

    const filtroDe = (id: string) => d.config.filtros.find(f => f.id === id)
    const definirFiltro = (id: string, novo: FiltroDoDisparo | null) =>
      mudar({ config: { filtros: [...d.config.filtros.filter(f => f.id !== id), ...(novo ? [novo] : [])] } })

    return (
      <div className="dp">
        <AvisoGuiaNovo guia="emails" dashboardId={dashboardId} />
        <div className="ng-cartao">
          <div className="ng-cartao-cab">
            <h2>{editando.id ? 'Editar disparo' : 'Novo disparo'}</h2>
            <div className="ng-acoes"><button type="button" className="ng-btn" onClick={fechar}>Voltar à lista</button></div>
          </div>

          <div className="ng-campos">
            <label className="ng-campo"><span>Nome</span>
              <input value={d.nome} maxLength={120} onChange={e => mudar({ nome: e.target.value })} placeholder="Resumo diário para a gerência" />
            </label>
            <label className="ng-campo"><span>Ambiente</span>
              <select value={d.ambienteId} onChange={e => mudar({ ambienteId: Number(e.target.value) })}>
                {opcoes.ambientes.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
              </select>
              <small>Os filtros padrão e a regra de cada pessoa valem como neste ambiente.</small>
            </label>
          </div>
          <label className="ng-campo"><span>Assunto</span>
            <input value={d.assunto} maxLength={200} onChange={e => mudar({ assunto: e.target.value })} />
            <small>Pode usar {'{{relatorio}}'}, {'{{data}}'}, {'{{usuario}}'} e {'{{texto:chave}}'} (um texto do HTML).</small>
          </label>
        </div>

        <div className="ng-cartao">
          <div className="ng-cartao-cab">
            <h2>HTML do e-mail</h2>
            <div className="ng-acoes">
              <button type="button" className="ng-btn" onClick={comecarDoModelo} disabled={trabalhando}>Começar do modelo</button>
              <button type="button" className="ng-btn" onClick={baixarDicionario}>Baixar dicionário</button>
              <button type="button" className="ng-btn" onClick={baixarGuia}>Baixar guia do e-mail</button>
            </div>
          </div>
          <p className="ng-explic">
            Cole o HTML que o Claude montou. Ele declara os blocos (colunas, medidas, rótulos, formatos) num JSON <code>portal-email</code> e usa
            marcadores no corpo; o portal desenha no servidor, na hora do envio. Para o Claude: mande o <b>guia do e-mail</b> e o <b>dicionário</b> (o
            mesmo do dashboard). Cores, fonte e logo vêm do template do relatório, por marcador.
          </p>
          <textarea className="rh-codigo dp-layout" value={d.layout} spellCheck={false} aria-label="HTML do e-mail"
                    onChange={e => mudar({ layout: e.target.value })} placeholder="Cole aqui o HTML do e-mail, ou comece do modelo." />
          {bloqueios.length > 0 && (
            <div className="rh-alerta erro">
              <b>Não dá para salvar enquanto houver:</b>
              <ul>{bloqueios.map((a, i) => <li key={i}>{a.mensagem}</li>)}</ul>
            </div>
          )}
          {avisos.length > 0 && (
            <div className="rh-alerta">
              <b>Atenção:</b>
              <ul>{avisos.map((a, i) => <li key={i}>{a.mensagem}</li>)}</ul>
            </div>
          )}
          {analise && analise.blocos.length > 0 && (
            <p className="ng-explic">Blocos no HTML: {analise.blocos.join(', ')}.</p>
          )}
        </div>

        <div className="ng-cartao">
          <div className="ng-cartao-cab"><h2>Quando sai</h2></div>
          <div className="ng-campos">
            <label className="ng-campo"><span>Frequência</span>
              <select value={d.frequencia} onChange={e => mudar({ frequencia: e.target.value as Frequencia })}>
                {(Object.keys(FREQUENCIAS) as Frequencia[]).map(f => <option key={f} value={f}>{FREQUENCIAS[f]}</option>)}
              </select>
            </label>
            <label className="ng-campo"><span>Horário (Brasília)</span>
              <input type="time" value={d.horario} onChange={e => mudar({ horario: e.target.value })} />
            </label>
          </div>
          {d.frequencia === 'semanal' && (
            <div className="dp-dias" role="group" aria-label="Dias da semana">
              {DIAS.map((nome, i) => (
                <button key={nome} type="button" aria-pressed={d.diasDaSemana.includes(i)}
                        onClick={() => mudar({ diasDaSemana: d.diasDaSemana.includes(i) ? d.diasDaSemana.filter(x => x !== i) : [...d.diasDaSemana, i] })}>{nome}</button>
              ))}
            </div>
          )}
          {d.frequencia === 'mensal' && (
            <label className="ng-campo" style={{ maxWidth: 220 }}><span>Dia do mês</span>
              <input type="number" min={1} max={31} value={d.diaDoMes ?? ''} onChange={e => mudar({ diaDoMes: e.target.value ? Number(e.target.value) : null })} />
              <small>Em mês mais curto, sai no último dia.</small>
            </label>
          )}
          <p className="ng-explic">{descreverRecorrencia(d)}</p>
        </div>

        {filtrosDoHtml.length > 0 && (
          <div className="ng-cartao">
            <div className="ng-cartao-cab"><h2>Filtros</h2></div>
            <p className="ng-explic">Os <code>filtros_tela</code> que o HTML declara, com valor fixo ou período. Cada bloco segue os que o HTML manda ele seguir. Sem escolher nada, vale tudo que a pessoa pode ver. O mesmo HTML pode ter vários disparos com recortes diferentes.</p>
            {filtrosDoHtml.map(f => {
              const atual = filtroDe(f.id)
              const modo = atual?.tipo ?? ''
              return (
                <div key={f.id} className="dp-filtro">
                  <b>{f.rotulo}</b>
                  <select className="ng-select" value={modo} aria-label={`Modo do filtro ${f.rotulo}`}
                          onChange={e => {
                            if (e.target.value === '') definirFiltro(f.id, null)
                            else if (e.target.value === 'relativo') definirFiltro(f.id, { id: f.id, tipo: 'relativo', periodo: 'ontem' })
                            else definirFiltro(f.id, { id: f.id, tipo: 'valores', valores: lerValores(valoresTexto[f.id] ?? '') })
                          }}>
                    <option value="">Não filtrar</option>
                    <option value="relativo">Período (anda com o calendário)</option>
                    <option value="valores">Valores fixos</option>
                  </select>
                  {atual?.tipo === 'relativo' && (
                    <select className="ng-select" value={atual.periodo ?? ''} aria-label={`Período do filtro ${f.rotulo}`}
                            onChange={e => definirFiltro(f.id, { ...atual, periodo: e.target.value })}>
                      {opcoes.periodos.map(p => <option key={p} value={p}>{PERIODOS[p] ?? p}</option>)}
                    </select>
                  )}
                  {atual?.tipo === 'valores' && (
                    <textarea className="dp-valores" rows={3} aria-label={`Valores do filtro ${f.rotulo}`} placeholder="Um valor por linha"
                              value={valoresTexto[f.id] ?? ''}
                              onChange={e => {
                                setValoresTexto(v => ({ ...v, [f.id]: e.target.value }))
                                definirFiltro(f.id, { id: f.id, tipo: 'valores', valores: lerValores(e.target.value) })
                              }} />
                  )}
                </div>
              )
            })}
          </div>
        )}

        <div className="ng-cartao">
          <div className="ng-cartao-cab"><h2>Quem recebe</h2></div>
          <p className="ng-explic">Só quem tem login no portal. Cada pessoa recebe os números no recorte da própria regra neste dashboard; quem não tem acesso ao relatório no ambiente não recebe.</p>
          <div className="dp-busca">
            <BuscaPessoa valor={null} rotulo="Adicionar destinatário"
                         aoEscolher={p => {
                           if (!p || d.destinatarios.includes(p.id)) return
                           setPessoas(x => ({ ...x, [p.id]: { userId: p.id, nome: p.nome, email: p.email } }))
                           mudar({ destinatarios: [...d.destinatarios, p.id] })
                         }} />
          </div>
          {d.destinatarios.length === 0 && <p className="ng-explic">Ninguém escolhido ainda.</p>}
          <div className="ng-chips">
            {d.destinatarios.map(id => (
              <span key={id} className="ng-chip" title={pessoas[id]?.email ?? ''}>
                {pessoas[id]?.nome ?? `Usuário ${id}`}
                <button type="button" className="ng-ico" aria-label={`Tirar ${pessoas[id]?.nome ?? id}`} onClick={() => mudar({ destinatarios: d.destinatarios.filter(x => x !== id) })}><CloseIcon fontSize="small" /></button>
              </span>
            ))}
          </div>
        </div>

        <CardBook book={d.config.book} usaBook={!!analise?.usaBook} dicionario={dicionario} periodos={opcoes.periodos}
                  aoMudar={b => mudar({ config: { ...d.config, book: b } })} />

        <div className="ng-cartao">
          <div className="ng-cartao-cab">
            <h2>Prévia</h2>
            <div className="ng-acoes"><button type="button" className="ng-btn" onClick={prever} disabled={trabalhando || !d.layout.trim() || bloqueios.length > 0}>{previa ? 'Atualizar prévia' : 'Pré-visualizar'}</button></div>
          </div>
          {!previa && <p className="ng-explic">Mostra o e-mail com dados reais, como você o receberia (administrador vê tudo; destinatário comum, o recorte da regra dele).</p>}
          {previa && (
            <>
              <p className="ng-explic"><b>Assunto:</b> {previa.assunto}</p>
              {previa.blocosComFalha > 0 && <div className="rh-alerta">{previa.blocosComFalha} bloco(s) não carregaram. No envio os valores deles saem como “—”.</div>}
              <iframe className="dp-previa" title="Prévia do e-mail" sandbox="" srcDoc={previa.html} />
            </>
          )}
        </div>

        <div className="ng-cartao">
          <label className="ng-toggle">
            <input type="checkbox" checked={d.ativo} onChange={e => mudar({ ativo: e.target.checked })} />
            <span>Disparo ativo (sai sozinho na recorrência)</span>
          </label>
          {faltas.length > 0 && <div className="rh-alerta"><ul>{faltas.map(f => <li key={f}>{f}</li>)}</ul></div>}
          <div className="rh-botoes">
            <button type="button" className="ng-btn prim" onClick={salvar} disabled={trabalhando || faltas.length > 0}>Salvar disparo</button>
            <button type="button" className="ng-btn" onClick={fechar}>Cancelar</button>
          </div>
        </div>
      </div>
    )
  }

  // ---------- lista ----------
  return (
    <div className="dp">
      <AvisoGuiaNovo guia="emails" dashboardId={dashboardId} />
      <div className="ng-cartao">
        <div className="ng-cartao-cab">
          <h2>Disparos de e-mail</h2>
          <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={() => abrir(null)} disabled={opcoes.ambientes.length === 0}>Novo disparo</button></div>
        </div>
        <p className="ng-explic">E-mails em HTML, feitos no Claude com o guia do e-mail, enviados na recorrência escolhida para quem tem login. Cada pessoa recebe os números no recorte da própria regra. Um dashboard pode ter vários disparos, com HTMLs diferentes.</p>
        {opcoes.ambientes.length === 0 && <div className="rh-alerta">O dashboard não está publicado em nenhum ambiente ativo.</div>}
        <div className="ng-tabela">
          <table>
            <thead><tr><th>Disparo</th><th>Quando</th><th className="n">Pessoas</th><th>Próximo envio</th><th>Situação</th><th /></tr></thead>
            <tbody>
              {lista.length === 0 && <tr><td colSpan={6} className="vazio">Nenhum disparo ainda.</td></tr>}
              {lista.map(d => (
                <tr key={d.id}>
                  <td><b>{d.nome}</b><small style={{ display: 'block', color: '#8b92a1' }}>{d.ambienteNome}</small></td>
                  <td>{descreverRecorrencia(d)}</td>
                  <td className="n">{d.destinatarios.length}</td>
                  <td>{d.ativo ? dataHora(d.proximoEnvioEm) : '—'}</td>
                  <td>
                    <span className={`ng-pill ${d.ativo ? 'ok' : 'inativo'}`}>{d.ativo ? 'Ativo' : 'Pausado'}</span>
                    {d.emProcessamento && <span className="ng-pill processando" style={{ marginLeft: 6 }}>Processando</span>}
                  </td>
                  <td className="dp-acoes">
                    <button type="button" className="ng-btn" onClick={() => abrir(d)}>Editar</button>
                    <button type="button" className="ng-btn" disabled={trabalhando} onClick={() => alternar(d)}>{d.ativo ? 'Pausar' : 'Ativar'}</button>
                    <button type="button" className="ng-btn" disabled={trabalhando || d.emProcessamento} onClick={() => testar(d)}>{d.emProcessamento ? 'Processando…' : 'Testar'}</button>
                    <button type="button" className="ng-btn" disabled={trabalhando} onClick={() => verHistorico(d)}>Histórico</button>
                    <button type="button" className="ng-btn perigo" onClick={() => setExcluir(d)}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {historico && (
        <div className="ng-cartao">
          <div className="ng-cartao-cab">
            <h2>Histórico · {historico.disparo.nome}</h2>
            <div className="ng-acoes"><button type="button" className="ng-btn" onClick={() => setHistorico(null)}>Fechar</button></div>
          </div>
          <div className="ng-tabela">
            <table>
              <thead><tr><th>Quando</th><th>Pessoa</th><th>Resultado</th><th>Detalhe</th></tr></thead>
              <tbody>
                {historico.envios.length === 0 && <tr><td colSpan={4} className="vazio">Nada enviado ainda.</td></tr>}
                {historico.envios.map((e, i) => (
                  <tr key={i}>
                    <td>{dataHora(e.quando)}</td>
                    <td>{e.nome ?? 'Disparo todo'}</td>
                    <td><span className={`ng-pill ${STATUS_ENVIO[e.status]?.classe ?? ''}`}>{STATUS_ENVIO[e.status]?.rotulo ?? e.status}</span></td>
                    <td>{e.detalhe ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Janela aberto={!!excluir} titulo={`Excluir o disparo “${excluir?.nome ?? ''}”`} botao="Excluir" perigo salvando={trabalhando}
              aoConfirmar={apagar} aoFechar={() => setExcluir(null)}>
        <p className="ng-explic">O disparo e o histórico dele somem. Os e-mails já enviados não voltam.</p>
      </Janela>
    </div>
  )
}
